// @flow
import api from 'api'
import { deletePdfDocument } from 'api/pdfFillerApi'
import { useForm } from 'hooks/useForm'
import { useUpdateURLSearchParams } from 'hooks/useQuery'
import { useDeletePackets } from 'queries/packets/useDeletePackets'
import * as React from 'react'
import { toast } from 'react-toastify'
import { ZentakeContext } from 'utils/ZentakeContext'

type Props = {
  close?: () => void,
  disabled?: ?boolean,
  isPacket?: boolean,
  resourceEnabled: boolean,
  resourceId: string | number,
  setLoading: (loading: boolean) => void,
  resourceType: 'form' | 'packet' | 'pdf',
  measuresEnabled: boolean,
}

function FormControlsDropdownMenuDeleteButton({
  close,
  disabled,
  isPacket,
  resourceEnabled,
  resourceId,
  setLoading,
  resourceType,
  measuresEnabled,
}: Props): React.Node {
  const { setAlertConfig, teamDetails } = React.useContext(ZentakeContext)
  const { fetchFormPage, startLoading, stopLoading } = useForm()
  const { deletePacket } = useDeletePackets()
  const updateParams = useUpdateURLSearchParams()

  const measuresEnabledTeam = teamDetails?.measures

  let description

  if (!isPacket) {
    description = (
      <>
        <p>
          Note: This form will be removed from all packets it is associated
          with.
        </p>
        {measuresEnabledTeam && measuresEnabled ? (
          <p className="mt-2 items-center justify-center rounded border !border-orange-200 bg-orange-50 p-2 text-[#ffaa00]">
            All tracked data related to this assessment will be permanently
            lost.
          </p>
        ) : null}
      </>
    )
  }

  const showDeleteConfirmation = React.useCallback(
    async () =>
      new Promise((resolve) =>
        setAlertConfig({
          title: `Are you sure you want to permanently delete this ${
            isPacket ? 'packet' : 'form'
          }?`,
          description,
          buttons: [
            {
              title: 'Delete',
              style: 'primary',
              danger: true,
              onPress: () => resolve(true),
            },
            {
              title: 'Cancel',
              onPress: () => resolve(false),
            },
          ],
          canDismiss: false,
        })
      ),
    [description, isPacket, setAlertConfig]
  )

  const handleDelete = React.useCallback(async () => {
    const deleteResource = isPacket
      ? deletePacket
      : resourceType === 'pdf'
        ? deletePdfDocument
        : api.deleteForm

    const confirm = await showDeleteConfirmation()

    if (!confirm) {
      return
    }

    try {
      setLoading(true)
      startLoading()
      // $FlowFixMe
      await deleteResource(resourceId)
      if (isPacket) {
        updateParams(['page', '1'])
      } else {
        await fetchFormPage()
      }
      toast.success(`The ${isPacket ? 'packet' : 'form'} has been deleted.`)
      if (close) {
        close()
      }
    } catch (error) {
      toast.error('An error occurred while deleting the resource.')
    } finally {
      setLoading(false)
      stopLoading()
    }
  }, [
    close,
    deletePacket,
    fetchFormPage,
    isPacket,
    resourceId,
    resourceType,
    setLoading,
    showDeleteConfirmation,
    startLoading,
    stopLoading,
    updateParams,
  ])

  return (
    <button
      className="block w-full px-4 py-2 text-center text-sm leading-5 text-gray-700 transition duration-150 ease-in-out hover:bg-gray-100 focus:outline-none"
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        handleDelete()
      }}
      type="button"
    >
      Delete
    </button>
  )
}

export default FormControlsDropdownMenuDeleteButton
